import {useIntl} from 'react-intl'
import {MessageItemCommonProps, MessageOrderInfo} from '../definitions'
import {useMemo, useState} from 'react'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useChatbotContext} from '../context'
import {DIMENSION_UNIT, WEIGHT_UNIT} from '../../../../_gori/constants'

const REGEX_ZIP_CODE = /^[0-9]{5}(?:-[0-9]{4})?$/i
const REGEX_VALID_ADDRESS = /^[\p{L}\p{N}\s,.'\-/#()]+$/u

export const useOrderInfo = ({message}: MessageItemCommonProps<MessageOrderInfo>) => {
  const {stringYup, numberYup} = UseYupValidate()
  const intl = useIntl()
  const {handleCreateMessage, disabledUserAction} = useChatbotContext()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [addressError, setAddressError] = useState<{
    from: string
    to: string
  }>({from: '', to: ''})

  const addressValidationSchema = useMemo(() => {
    return {
      street1: stringYup(255, 'ADDRESS').test(
        'is-valid-address',
        intl.formatMessage({id: 'ORDER_INFO_ADDRESS_INVALID'}),
        (value) => {
          if (!value) return false
          const trimmed = value.trim()
          const isZip = REGEX_ZIP_CODE.test(trimmed)
          const isValidAddress = REGEX_VALID_ADDRESS.test(trimmed)
          return isZip || isValidAddress
        }
      ),
    }
  }, [intl, stringYup])
  const dimensionValidationSchema = useMemo(
    () => ({
      length: numberYup.parcel('length'),
      width: numberYup.parcel('width'),
      height: numberYup.parcel('height'),
      weight: numberYup.parcel('weight'),
      dimension_unit: stringYup(50, 'UNIT'),
      weight_unit: stringYup(50, 'UNIT'),
    }),
    [numberYup, stringYup]
  )

  const convertToString = (value?: number | string, defaultValue: string = '') => {
    if (!value) return defaultValue
    return typeof value === 'number' ? value.toString() : value
  }

  const initialValues = useMemo(() => {
    return {
      from: {
        street1: message.content?.from?.street1 || '',
        city: message.content?.from?.city || '',
        state: message.content?.from?.state || '',
        country: message.content?.from?.country || '',
        zip: message.content?.from?.zip || '',
      },
      to: {
        street1: message.content?.to?.street1 || '',
        city: message.content?.to?.city || '',
        state: message.content?.to?.state || '',
        country: message.content?.to?.country || '',
        zip: message.content?.to?.zip || '',
      },
      dimension: [
        {
          length: message.content?.dimension?.[0]?.length || '',
          width: message.content?.dimension?.[0]?.width || '',
          height: message.content?.dimension?.[0]?.height || '',
          dimension_unit: message.content?.dimension?.[0]?.dimension_unit || DIMENSION_UNIT.IN,
          weight_unit: message.content?.dimension?.[0]?.weight_unit || WEIGHT_UNIT.OZ,
          weight: message.content?.dimension?.[0]?.weight || '',
        },
      ],
    }
  }, [message])

  const formik = useFormik<MessageOrderInfo>({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      from: Yup.object().shape(addressValidationSchema),
      to: Yup.object().shape(addressValidationSchema),
      dimension: Yup.array().of(Yup.object().shape(dimensionValidationSchema)),
    }),
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (addressError.from || addressError.to) {
        return
      }

      try {
        handleCreateMessage({
          type: message.type,
          message: {
            ...values,
            dimension: values.dimension.map((dim) => ({
              length: convertToString(dim.length),
              width: convertToString(dim.width),
              height: convertToString(dim.height),
              dimension_unit: convertToString(dim.dimension_unit, DIMENSION_UNIT.IN),
              weight_unit: convertToString(dim.weight_unit, WEIGHT_UNIT.OZ),
              weight: convertToString(dim.weight),
            })),
          } as MessageOrderInfo,
          currentMessageId: message.id,
        })
        setIsSubmitted(true)
      } catch {
        setIsSubmitted(false)
      }
    },
  })

  const disabled = useMemo(
    () => message?.content?.disabled || disabledUserAction || isSubmitted,
    [disabledUserAction, message?.content?.disabled, isSubmitted]
  )

  const handleSetAddressError = (type: 'from' | 'to', errorMessage: string) => {
    setAddressError((prev) => ({
      ...prev,
      [type]: errorMessage,
    }))
  }

  const resetForm = () => {
    formik.resetForm()
    setAddressError({from: '', to: ''})
  }

  return {
    isSubmitted,
    setIsSubmitted,
    formik,
    disabled,
    addressError,
    setAddressError: handleSetAddressError,
    resetForm,
  }
}
